/**
 * Custom hook encapsulating champion and loadout selection state (#1154).
 *
 * Manages the coupled lifecycle:
 * - Selected champion (`championId`)
 * - Selected ability loadout (`loadout`)
 * - Switching champion automatically resets `loadout` to `defaultLoadout(newChampionId)`
 * - Loadout validity check via `isValidLoadoutSize(championId, loadout)`
 * - Programmatic `reset(options?)` to synchronize with external state updates
 */

import { useCallback, useState } from "react";
import type { AbilityId, ChampionId } from "@pit/protocol";
import { DEFAULT_CHAMPION_ID } from "./catalog.js";
import { defaultLoadout } from "./loadout.js";
import { isValidLoadoutSize } from "./LoadoutPicker.js";

export interface ChampionLoadoutStateOptions {
  readonly initialChampionId?: ChampionId;
  readonly initialLoadout?: readonly AbilityId[];
}

export interface ChampionLoadoutResetOptions {
  readonly championId?: ChampionId;
  readonly loadout?: readonly AbilityId[];
}

export interface ChampionLoadoutState {
  readonly championId: ChampionId;
  readonly setChampionId: (id: ChampionId) => void;
  readonly loadout: readonly AbilityId[];
  readonly setLoadout: (loadout: readonly AbilityId[]) => void;
  readonly isValid: boolean;
  readonly resetToDefault: () => void;
  readonly reset: (options?: ChampionLoadoutResetOptions) => void;
}

export function useChampionLoadoutState(
  options?: ChampionLoadoutStateOptions,
): ChampionLoadoutState {
  const [championId, setChampionIdState] = useState<ChampionId>(
    () => options?.initialChampionId ?? DEFAULT_CHAMPION_ID,
  );
  const [loadout, setLoadout] = useState<readonly AbilityId[]>(() => {
    if (options?.initialLoadout !== undefined) {
      return options.initialLoadout;
    }
    const champ = options?.initialChampionId ?? DEFAULT_CHAMPION_ID;
    return defaultLoadout(champ);
  });

  const setChampionId = useCallback((id: ChampionId) => {
    setChampionIdState(id);
    setLoadout(defaultLoadout(id));
  }, []);

  const resetToDefault = useCallback(() => {
    setLoadout(defaultLoadout(championId));
  }, [championId]);

  const reset = useCallback(
    (resetOptions?: ChampionLoadoutResetOptions) => {
      const nextChampionId = resetOptions?.championId ?? championId;
      setChampionIdState(nextChampionId);
      if (resetOptions?.loadout !== undefined) {
        setLoadout(resetOptions.loadout);
      } else {
        setLoadout(defaultLoadout(nextChampionId));
      }
    },
    [championId],
  );

  const isValid = isValidLoadoutSize(championId, loadout);

  return {
    championId,
    setChampionId,
    loadout,
    setLoadout,
    isValid,
    resetToDefault,
    reset,
  };
}
